//@ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAlert } from 'react-alert';
import { useDispatch } from 'react-redux';
import { Form, Alert, InputGroup, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWarehouse,
  faPlus,
  faMinus,
} from '@fortawesome/free-solid-svg-icons';
import { addMaterialToCutting } from 'services/apiRequests/cutting/update';
import { getStorageItems } from 'services/apiRequests/storage.api';
import { setSpinner } from 'services/store/actions/view';
import Modal from 'PageTemplates/ModalTemplate';

const AddMaterialModal = ({ cuttingPackage, closeModal, onSuccess }) => {
  const dispatch = useDispatch();
  const alert = useAlert();

  const [selectedItemId, setSelectedItemId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState('');
  const [availableItems, setAvailableItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAvailableItems();
  }, []);

  const fetchAvailableItems = async () => {
    try {
      const response = await getStorageItems();
      if (response?.items) {
        // Filtruj tylko te materiały, które nie zostały jeszcze użyte w cięciu
        const usedMaterialIds =
          cuttingPackage.materialsUsed?.map(
            (material) => material.storageItem._id || material.storageItem,
          ) || [];

        const availableItems = response.items.filter(
          (item) => !usedMaterialIds.includes(item._id),
        );

        setAvailableItems(availableItems);
      }
    } catch (error) {
      console.error('Błąd pobierania elementów magazynowych:', error);
      alert.error('Błąd podczas pobierania elementów magazynowych');
    }
  };

  const selectedItem = availableItems.find(
    (item) => item._id === selectedItemId,
  );
  const canAdd =
    selectedItem &&
    quantity > 0 &&
    quantity <= selectedItem.quantity &&
    availableItems.length > 0;

  const handleSubmit = async () => {
    if (!selectedItemId) {
      setError('Wybierz materiał');
      return;
    }

    if (quantity <= 0) {
      setError('Ilość musi być większa od 0');
      return;
    }

    if (!selectedItem) {
      setError('Nie znaleziono wybranego materiału');
      return;
    }

    if (quantity > selectedItem.quantity) {
      setError(
        `Niewystarczająca ilość w magazynie. Dostępne: ${selectedItem.quantity}`,
      );
      return;
    }

    setLoading(true);
    setError('');

    try {
      dispatch(setSpinner(true));
      await addMaterialToCutting(
        cuttingPackage._id,
        selectedItemId,
        quantity,
        reason,
      );
      dispatch(setSpinner(false));

      alert.success(
        `Dodano materiał: ${selectedItem.name} (${quantity} ${
          selectedItem.unit || 'szt.'
        })`,
      );

      if (onSuccess) onSuccess();
      closeModal();
    } catch (error) {
      dispatch(setSpinner(false));
      setError(
        error.response?.data?.message ||
          'Błąd podczas dodawania materiału',
      );
    } finally {
      setLoading(false);
    }
  };

  const incrementQuantity = () => {
    const newValue = quantity + 1;
    if (selectedItem && newValue <= selectedItem.quantity) {
      setQuantity(newValue);
    }
  };

  const decrementQuantity = () => {
    const newValue = Math.max(1, quantity - 1);
    setQuantity(newValue);
  };

  return (
    <Modal
      title="Dodaj materiał do cięcia"
      closeModal={closeModal}
      onOk={handleSubmit}
      okButtonText={loading ? 'Dodawanie...' : 'Dodaj materiał'}
      disabled={loading || !canAdd}
      backdrop="static"
      size="md"
    >
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="mb-3">
        <Form.Label>Wybierz materiał</Form.Label>
        <Form.Select
          value={selectedItemId}
          onChange={(e) => {
            setSelectedItemId(e.target.value);
            setQuantity(1); // Reset quantity when changing item
          }}
        >
          <option value="">
            {availableItems.length > 0
              ? 'Wybierz materiał z magazynu...'
              : 'Brak dostępnych materiałów do dodania'}
          </option>
          {availableItems.map((item) => (
            <option key={item._id} value={item._id}>
              {item.name} ({item.type || 'Brak typu'}) - Dostępne:{' '}
              {item.quantity} {item.unit || 'szt.'}
            </option>
          ))}
        </Form.Select>
        {availableItems.length === 0 && (
          <Form.Text className="text-muted">
            Wszystkie dostępne materiały zostały już dodane do tego pakietu
            cięcia
          </Form.Text>
        )}
      </div>

      {selectedItem && (
        <>
          <div className="mb-3">
            <div className="bg-light p-3 rounded">
              <div className="row">
                <div className="col-8">
                  <h6 className="mb-1">{selectedItem.name}</h6>
                  <small className="text-muted">
                    <FontAwesomeIcon icon={faWarehouse} className="me-1" />
                    Typ: {selectedItem.type || 'Brak typu'}
                  </small>
                </div>
                <div className="col-4 text-end">
                  <div className="text-success">
                    <strong>Dostępne:</strong>
                    <div className="h5 mb-0">
                      {selectedItem.quantity} {selectedItem.unit || 'szt.'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <Form.Label>Ilość do pobrania</Form.Label>
            <InputGroup>
              <Button
                variant="outline-secondary"
                onClick={decrementQuantity}
                disabled={quantity <= 1}
              >
                <FontAwesomeIcon icon={faMinus} />
              </Button>
              <Form.Control
                type="number"
                value={quantity}
                onChange={(e) =>
                  setQuantity(parseFloat(e.target.value) || 0)
                }
                min="1"
                max={selectedItem.quantity}
                step="1"
                className="text-center"
              />
              <Button
                variant="outline-secondary"
                onClick={incrementQuantity}
                disabled={quantity >= selectedItem.quantity}
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </InputGroup>
            {quantity > selectedItem.quantity && (
              <Form.Text className="text-danger">
                Niewystarczająca ilość w magazynie!
              </Form.Text>
            )}
          </div>

          <div className="mb-3">
            <Form.Label>Powód pobrania (opcjonalnie)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Np. dodatkowy materiał, wymiana uszkodzonego..."
            />
          </div>
        </>
      )}

      <Alert variant="info">
        <small>
          <FontAwesomeIcon icon={faWarehouse} className="me-1" />
          Materiał zostanie pobrany z magazynu i dodany do listy materiałów
          używanych w cięciu.
        </small>
      </Alert>
    </Modal>
  );
};

export default AddMaterialModal;
